import { ReactElement, useCallback, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from 'antd';
import { ConfirmDeleteModal, CrudMode, DeleteErrorException, ValueLists } from '@ohpen/enso-ui';

import { BdmResponse, RegionResponse, regionDelete, regionReplace } from '@pmp/api';

import { getApiErrorDetail } from '@pmp/utils';

import { getInitialState, reducer } from '../reducer';
import { BDMS_LIST, REGIONS_LIST } from '../regionsBdmsTableConfig';
import { RegionsList } from './RegionsList';
import { RegionCrudForm } from './RegionCrudForm';
import { ReplaceForm } from './ReplaceForm';

interface Props {
  regions: RegionResponse[];
  bdms: BdmResponse[];
  loading: boolean;
  refresh: () => void;
}

export const RegionPanel = ({ regions, bdms, loading, refresh }: Props): ReactElement => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer<RegionResponse>, getInitialState<RegionResponse>());
  const [replace, setReplace] = useState<{ open: boolean; record: RegionResponse | null }>({ open: false, record: null });

  const bdmNameById = useMemo(() => new Map(bdms.map(b => [b.id, b.name])), [bdms]);
  const regionNameById = useMemo(() => new Map(regions.map(r => [r.id, r.name])), [regions]);

  const valueLists: ValueLists = useMemo(
    () => ({
      [BDMS_LIST]: Object.fromEntries(bdms.map(b => [b.id, b.name])),
      [REGIONS_LIST]: Object.fromEntries(regions.map(r => [r.id, r.name])),
    }),
    [bdms, regions],
  );

  const deleteRecord = state.deleteModal.record;
  const onConfirmDelete = useCallback(async () => {
    if (!deleteRecord) return { description: '' };
    try {
      await regionDelete({ path: { id: deleteRecord.id } });
    } catch (error) {
      dispatch({ type: 'closeDeleteModal' });
      return Promise.reject(new DeleteErrorException(getApiErrorDetail(error) ?? t('lender.regionsBdms.labels.regionDeleteBlockedDetail')));
    }
    refresh();
    dispatch({ type: 'closeDeleteModal' });
    return { description: t('lender.regionsBdms.labels.regionDeleted', { name: deleteRecord.name }) };
  }, [deleteRecord, refresh, t]);

  const replaceRecord = replace.record;

  return (
    <>
      <RegionsList
        data={regions}
        valueLists={valueLists}
        loading={loading}
        canReplace={regions.length >= 2}
        onCreate={() => dispatch({ type: 'openCrud', mode: CrudMode.CREATE })}
        onEdit={record => dispatch({ type: 'openCrud', mode: CrudMode.EDIT, record })}
        onReplace={record => setReplace({ open: true, record })}
        onDelete={record => dispatch({ type: 'openDeleteModal', record })}
      />

      {state.crud.open && (
        <RegionCrudForm
          mode={state.crud.mode}
          record={state.crud.record}
          bdmNameById={bdmNameById}
          onClose={() => dispatch({ type: 'closeCrud' })}
          refreshTable={refresh}
        />
      )}

      {replace.open && replaceRecord && (
        <ReplaceForm
          title={t('lender.regionsBdms.labels.replaceRegion', { name: replaceRecord.name })}
          explanation={t('lender.regionsBdms.labels.replaceRegionExplanation')}
          replacementLabelKey="lender.regionsBdms.labels.replacementRegion"
          requiredMessage={t('lender.regionsBdms.labels.replacementRegionRequired')}
          options={regions.filter(r => r.id !== replaceRecord.id).map(r => ({ label: r.name, value: r.id }))}
          onReplace={replacementRegionId => regionReplace({ path: { id: replaceRecord.id }, body: { replacementRegionId } })}
          successMessage={rid =>
            t('lender.regionsBdms.labels.regionReplaced', { name: replaceRecord.name, replacement: regionNameById.get(rid) ?? '' })
          }
          onClose={() => setReplace({ open: false, record: null })}
          refreshTable={refresh}
        />
      )}

      <ConfirmDeleteModal
        open={state.deleteModal.open}
        title={t('lender.regionsBdms.labels.deleteRegion')}
        okText={t('enso.buttons.delete')}
        onConfirm={onConfirmDelete}
        onCancel={() => dispatch({ type: 'closeDeleteModal' })}
      >
        {deleteRecord && (
          <>
            <Typography.Paragraph>{t('lender.regionsBdms.labels.deleteRegionConfirm', { name: deleteRecord.name })}</Typography.Paragraph>
            <Typography.Paragraph>{t('lender.regionsBdms.labels.noChangeRequestWarning')}</Typography.Paragraph>
          </>
        )}
      </ConfirmDeleteModal>
    </>
  );
};
